import { supabase } from '../supabaseClient';

export const heroService = {
  async uploadHeroImage(file, editionYear) {
    try {
      // Create unique filename
      const timestamp = Date.now();
      const fileExt = file.name.split('.').pop();
      const fileName = `hero-bg-${timestamp}.${fileExt}`;
      const filePath = `${editionYear}/${fileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('hero-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('hero-images')
        .getPublicUrl(filePath);

      return {
        path: filePath,
        url: publicUrl
      };
    } catch (error) {
      console.error('Error uploading hero image:', error);
      throw error;
    }
  },

  async deleteHeroImage(imagePath) {
    try {
      if (!imagePath) return;

      const { error } = await supabase.storage
        .from('hero-images')
        .remove([imagePath]);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting hero image:', error);
      throw error;
    }
  },

  async getHeroData(editionYear) {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      // Get hero data
      const { data: heroData } = await supabase
        .from('hero_data')
        .select('*')
        .eq('edition_id', edition.id)
        .single();

      if (!heroData) {
        return null;
      }

      return {
        badge: {
          date: heroData.badge_date || '',
          location: heroData.badge_location || ''
        },
        title: heroData.title || '',
        subtitle: heroData.subtitle || '',
        description: heroData.description || '',
        primaryButton: {
          text: heroData.primary_button_text || '',
          action: heroData.primary_button_action || ''
        },
        secondaryButton: {
          text: heroData.secondary_button_text || '',
          action: heroData.secondary_button_action || '',
          visible: heroData.secondary_button_visible || false
        },
        backgroundImage: heroData.background_image_url || '',
        backgroundImagePath: heroData.background_image_path || '',
        scrollIndicator: {
          visible: heroData.scroll_indicator_visible || false,
          text: heroData.scroll_indicator_text || ''
        }
      };
    } catch (error) {
      console.error('Error getting hero data:', error);
      throw error;
    }
  },

  async updateHeroData(heroData, editionYear) {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      const updateData = {
        edition_id: edition.id,
        badge_date: heroData.badge?.date || '',
        badge_location: heroData.badge?.location || '',
        title: heroData.title || '',
        subtitle: heroData.subtitle || '',
        description: heroData.description || '',
        primary_button_text: heroData.primaryButton?.text || '',
        primary_button_action: heroData.primaryButton?.action || '',
        secondary_button_text: heroData.secondaryButton?.text || '',
        secondary_button_action: heroData.secondaryButton?.action || '',
        secondary_button_visible: heroData.secondaryButton?.visible || false,
        background_image_url: heroData.backgroundImage || '',
        background_image_path: heroData.backgroundImagePath || '',
        scroll_indicator_visible: heroData.scrollIndicator?.visible || false,
        scroll_indicator_text: heroData.scrollIndicator?.text || '',
        updated_at: new Date().toISOString()
      };

      // Try to update existing record first
      const { data: existingData } = await supabase
        .from('hero_data')
        .select('id')
        .eq('edition_id', edition.id)
        .single();

      if (existingData) {
        // Update existing record
        const { data, error } = await supabase
          .from('hero_data')
          .update(updateData)
          .eq('id', existingData.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Insert new record
        const { data, error } = await supabase
          .from('hero_data')
          .insert([updateData])
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    } catch (error) {
      console.error('Error updating hero data:', error);
      throw error;
    }
  }
};
